import Dexie from 'dexie'
import { addBasketEntry, db, enqueueOp } from './db'
import type { CaptureMethod } from '../api/contracts'
import type { BasketEntry, OutboxOp } from './types'

/**
 * The write path. Every scan lands here and nowhere else, because the ordering and undo
 * rules below are only correct if there is exactly one way into the outbox.
 */

export interface ScanInput {
  palletCode: string
  serialNumber: string
  captureMethod: CaptureMethod
  /** Set only by the «انتقال سریع» confirmation, which mints a fresh op. */
  overrideExisting?: boolean
}

export function queueAdd(input: ScanInput): Promise<OutboxOp> {
  return enqueueOp({
    kind: 'add',
    palletCode: input.palletCode,
    serialNumbers: [input.serialNumber],
    overrideExisting: input.overrideExisting ?? false,
    captureMethod: input.captureMethod,
    capturedAt: new Date().toISOString(),
  })
}

export function queueRemove(input: ScanInput): Promise<OutboxOp> {
  return enqueueOp({
    kind: 'remove',
    palletCode: input.palletCode,
    serialNumbers: [input.serialNumber],
    // Meaningless for a removal; the server ignores it on this path.
    overrideExisting: false,
    captureMethod: input.captureMethod,
    capturedAt: new Date().toISOString(),
  })
}

/**
 * Enqueues a basket assignment as one operation per serial.
 *
 * The backend accepts a batch, but collapsing the basket into one outbox row would make
 * Queue's operation-scoped retry/delete actions unsafe for a single rejected serial.
 * Keeping the rows separate preserves the idempotency and triage invariants already used
 * by the pallet-first flow.
 */
export async function queueBasketAssign(
  palletCode: string,
): Promise<{ operations: OutboxOp[]; entries: BasketEntry[] }> {
  const entries = await db.basket.orderBy('seq').toArray()
  const operations: OutboxOp[] = []

  for (const entry of entries) {
    operations.push(
      await enqueueOp({
        kind: 'assign',
        palletCode,
        serialNumbers: [entry.serialNumber],
        overrideExisting: false,
        captureMethod: entry.captureMethod,
        capturedAt: entry.capturedAt,
        basketSeq: entry.seq,
      }),
    )
  }

  return { operations, entries }
}

/**
 * «انتقال سریع» — the operator confirmed moving a serial off another pallet.
 *
 * A NEW op with a NEW id, never a flip of overrideExisting on the rejected one. The
 * server's idempotency store keys on the key alone and checks it before calling the ERP,
 * so replaying the old key would return the stored belongs_to_other_pallet rejection and
 * the transfer would silently do nothing. The rejected op is dropped, since the operator
 * has answered the question it was asking.
 */
export async function queueTransfer(rejectedOpId: string, input: ScanInput): Promise<OutboxOp> {
  const op = await queueAdd({ ...input, overrideExisting: true })
  await db.outbox.delete(rejectedOpId)

  return op
}

/**
 * Returns the rejected operation to the worker without changing its id or serial list.
 * Keeping the original row is what makes a retry replay the same idempotent request.
 */
export async function retryRejectedOp(rejectedOpId: string): Promise<void> {
  const op = await db.outbox.get(rejectedOpId)

  if (op?.status !== 'rejected') {
    return
  }

  await db.outbox.update(rejectedOpId, {
    status: 'queued',
    nextAttemptAt: 0,
    lastError: undefined,
    ignoredSerialNumbers: undefined,
  })
}

/** Drops a rejected operation that never reached a successful state. */
export function deleteRejectedOp(rejectedOpId: string): Promise<void> {
  return db.outbox.delete(rejectedOpId)
}

/**
 * Removes one rejected serial from triage without changing the pallet or the operation's
 * idempotency identity. The remaining rejected serials, if any, stay visible.
 */
export async function ignoreRejectedSerial(
  rejectedOpId: string,
  serialNumber: string,
): Promise<void> {
  const op = await db.outbox.get(rejectedOpId)

  if (op?.status !== 'rejected') {
    return
  }

  const ignored = new Set(op.ignoredSerialNumbers ?? [])
  ignored.add(serialNumber)
  await db.outbox.update(rejectedOpId, { ignoredSerialNumbers: [...ignored] })
}

/**
 * Called by the future sync worker after a successful response so queue/home can show
 * the actual completion time rather than the time an operator scanned.
 */
export async function recordSuccessfulSync(
  opId: string,
  syncedAt: number = Date.now(),
): Promise<void> {
  await db.outbox.update(opId, { syncedAt })
}

export type UndoOutcome =
  /** The op never reached the server, so it was deleted outright. */
  | { kind: 'deleted'; serialNumber: string }
  /** The server already has it, so a compensating operation was queued. */
  | { kind: 'compensated'; serialNumber: string }
  | { kind: 'nothing' }

/**
 * «لغو آخرین اسکن» means two different things, and conflating them corrupts the pallet:
 *
 *  - a still-queued add is DELETED. Sending a compensating remove for something the
 *    server never saw would be a removal of a serial that is not on the pallet, which
 *    the ERP rejects and the operator cannot act on.
 *  - an already-synced add ENQUEUES a remove, because the only way to undo something the
 *    server has accepted is to tell the server.
 *
 * The same split applies in reverse to an undone removal.
 */
export async function undoLast(palletCode: string): Promise<UndoOutcome> {
  return db.transaction('rw', db.outbox, async () => {
    const last = await db.outbox
      .where('[palletCode+seq]')
      .between([palletCode, Dexie.minKey], [palletCode, Dexie.maxKey])
      .last()

    if (!last) {
      return { kind: 'nothing' }
    }

    const serialNumber = last.serialNumbers[last.serialNumbers.length - 1] ?? ''

    if (last.status === 'queued') {
      await db.outbox.delete(last.id)
      return { kind: 'deleted', serialNumber }
    }

    // 'inflight' counts as synced: the request may already have been accepted, and the
    // response simply not received. Deleting it would lose a serial the server holds.
    const compensation = last.kind === 'remove' ? queueAdd : queueRemove

    await compensation({
      palletCode,
      serialNumber,
      captureMethod: last.captureMethod,
    })

    return { kind: 'compensated', serialNumber }
  })
}

/**
 * Capacity the device can compute without the network: the last count the server
 * confirmed, plus what is still queued.
 *
 * This number is for display only. The «ظرفیت پالت پر شده است» dialog fires on a server
 * `pallet_full` reject and never on this arithmetic, because the device cannot know what
 * another handheld put on the same pallet while this one was offline.
 */
export function derivedItemCount(
  confirmed: { itemCount: number } | undefined,
  queued: OutboxOp[],
): number {
  const base = confirmed?.itemCount ?? 0

  return queued.reduce((count, op) => {
    const delta = op.serialNumbers.length

    return op.kind === 'remove' ? count - delta : count + delta
  }, base)
}

/** Serial-first flow: a scan goes to the basket, not to an outbox op. */
export function addToBasket(serialNumber: string, captureMethod: CaptureMethod) {
  return addBasketEntry({ serialNumber, captureMethod, capturedAt: new Date().toISOString() })
}

/** Removes the real last basket entry, using the persisted monotonic sequence. */
export async function undoBasketLast(): Promise<BasketEntry | null> {
  return db.transaction('rw', db.basket, async () => {
    const last = await db.basket.orderBy('seq').last()

    if (last === undefined) {
      return null
    }

    await db.basket.delete(last.serialNumber)
    return last
  })
}

/**
 * Removes only the rows that were assigned. A new scan with the same serial while the
 * assignment was waiting must not be deleted when the old assignment settles.
 */
export async function settleBasketAssignment(entries: BasketEntry[]): Promise<void> {
  await db.transaction('rw', db.basket, async () => {
    const current = await db.basket.bulkGet(entries.map((entry) => entry.serialNumber))
    const unchangedSerials = entries
      .filter((entry, index) => current[index]?.seq === entry.seq)
      .map((entry) => entry.serialNumber)

    await db.basket.bulkDelete(unchangedSerials)
  })
}
